import { GetStaticProps, GetStaticPaths, GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getAllPostSlugs, getPostData, PostProps } from '../../../lib/posts';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import PostContent from './PostContent';
import AboutContent from './AboutContent';
import { usePageViewEvent } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { COOKIE_STOP_REDIRECT } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { getCookie, setCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';

interface Slug {
  slug: string;
  locale: string;
  filename: string;
}

interface GroupedSlugs {
  [filename: string]: Slug[];
}

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const { params, locale } = context;
  const currentSlug = params?.slug as string;
  const postData = await getPostData(currentSlug, locale || 'es');
  const expectedSlug = postData.slug;

  // Fetch all slugs for all locales
  const allSlugs: Slug[] = getAllPostSlugs().map(slug => ({
    slug: slug.params.slug,
    locale: slug.params?.locale,
    filename: slug.params?.fileName,
  }));

  // Find the filename that corresponds to the currentSlug
  const currentFilename: string | undefined = allSlugs.find(
    slug => slug.slug === currentSlug,
  )?.filename;

  const groupedByFilename: GroupedSlugs = allSlugs.reduce((acc: GroupedSlugs, current: Slug) => {
    const { filename } = current;
    const newAcc = { ...acc };
    if (!newAcc[filename]) {
      newAcc[filename] = [];
    }
    newAcc[filename].push(current);
    return newAcc;
  }, {});

  const localeSlugs = groupedByFilename[currentFilename];

  return {
    props: {
      ...(await serverSideTranslations(locale || 'es', ['index'])),
      postData,
      currentSlug,
      localeSlugs,
      expectedSlug,
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = getAllPostSlugs();
  const paths = slugs.flatMap(slug => [
    { params: slug.params, locale: 'es' },
    { params: slug.params, locale: 'en' },
    { params: slug.params, locale: 'de' },
    { params: slug.params, locale: 'fr' },
  ]);

  return {
    paths,
    fallback: false,
  };
};

const Post = ({
  postData,
  currentSlug,
  localeSlugs,
  expectedSlug,
}: PostProps & {
  currentSlug: string;
  localeSlugs: { slug: string; locale: string; filename: string }[];
  expectedSlug: string;
}) => {
  const router = useRouter();
  usePageViewEvent();

  useEffect(() => {
    if (expectedSlug && currentSlug !== expectedSlug) {
      router.replace(`/${router.locale}/posts/${expectedSlug}`);
    }

    if (currentSlug === 'avis-et-temoignages-sofitel' && !expectedSlug) {
      const aboutSlug: { [key: string]: string } = {
        es: 'quienes-somos',
        en: 'about-us',
        de: 'uber-uns',
      };
      router.push(`/${router.locale}/posts/${aboutSlug[router.locale]}`);
    }
  }, [currentSlug, expectedSlug, router]);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const title = postData?.title || 'Sofitel Boutique';
  const description = postData?.description || 'Sofitel Boutique';
  const metaImage = postData?.image;
  const articleType = postData?.type;

  const hreflangTags = localeSlugs?.map(localeSlug => ({
    href: `${siteUrl}/${localeSlug?.locale}/posts/${localeSlug.slug}`,
    hreflang: localeSlug.locale,
  }));

  const enSlug = localeSlugs.find(slug => slug.locale === 'en')?.slug;
  const canonicalHref = `${siteUrl}/${router.locale}/posts/${currentSlug}`;
  const xDefaultHref =
    currentSlug === 'avis-et-temoignages-sofitel' ? canonicalHref : `${siteUrl}/en/posts/${enSlug}`;

  useEffect(() => {
    const rdflag = getCookie(COOKIE_STOP_REDIRECT);
    if (!rdflag) {
      // setting stop redirect cookie to prevent redirections
      setCookie(COOKIE_STOP_REDIRECT, '1');
    }
  }, []);

  useEffect(() => {
    const initializeEventListeners = () => {
      const showMoreButton = document.getElementById('show-more');
      const showLessButton = document.getElementById('show-less');

      const toggleContent = () => {
        document.querySelectorAll('.show-more, .show-more-content, .show-less').forEach(element => {
          element.classList.toggle('open');
        });
      };

      if (showMoreButton) showMoreButton.addEventListener('click', toggleContent);
      if (showLessButton) showLessButton.addEventListener('click', toggleContent);

      return () => {
        if (showMoreButton) showMoreButton.removeEventListener('click', toggleContent);
        if (showLessButton) showLessButton.removeEventListener('click', toggleContent);
      };
    };

    const cleanupEventListeners = initializeEventListeners();

    return () => {
      cleanupEventListeners();
    };
  }, [currentSlug]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="og:description" content={description} />
        <meta name="og:title" content={title} />
        <meta name="og:image" content={`https://sofitelboutique.com${metaImage}`} />
        <link rel="canonical" href={canonicalHref} />
        <link rel="alternate" hrefLang="x-default" href={xDefaultHref} />
        {hreflangTags.map(
          ({ href, hreflang }) =>
            href && (
              <link key={`${hreflang} ${href}`} rel="alternate" hrefLang={hreflang} href={href} />
            ),
        )}
      </Head>
      {articleType === 'about' ? (
        <AboutContent contentHtml={postData?.contentHtml} />
      ) : (
        <PostContent
          contentHtml={postData?.contentHtml}
          ctaIcon={postData?.ctaIcon}
          title={postData?.title}
          description={postData?.description}
          image={postData?.image}
        />
      )}
    </>
  );
};

export default Post;
